import { DOCUMENT, registerLocaleData } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, mergeMap, of } from 'rxjs';

import { APP_NAME, FE_API_VERSION, FE_VERSION } from '@shared/constants';
import { BreakpointService } from '@shared/services/breakpoint';
import { GtagService } from '@shared/services/gtag';
import { UserService } from '@shared/services/user';
import { CONFIG_TOKEN } from '@shared/tokens';
import { LOCALE_GB } from '@shared/utils/locales';

import { MetaDataActions, fromMetaData } from '@stores/meta-data';

@Injectable()
export class AppService {
  readonly #title = inject(Title);
  readonly #config = inject(CONFIG_TOKEN);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #userService = inject(UserService);
  readonly #breakpointService = inject(BreakpointService);
  readonly #store = inject(Store);
  readonly #document = inject(DOCUMENT);
  readonly #gtagService = inject(GtagService);

  readonly #isDesktop$ = this.#breakpointService.isMobile$.pipe(map((isMobile) => !isMobile));
  readonly #routerDataShowSideMenu$ = this.#router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map(() => {
      let route = this.#activatedRoute;
      while (route.firstChild) {
        route = route.firstChild;
      }
      return route;
    }),
    mergeMap((route) => route.data),
    map((data) => data['showSideMenu'] ?? true)
  );
  readonly #versions$ = this.#config.showVersion
    ? this.#store.select(fromMetaData.selectVersions$).pipe(
        map((versions) => ({
          beVersion: versions?.version,
          beApiVersion: versions?.apiVersion,
          feVersion: FE_VERSION,
          feApiVersion: FE_API_VERSION,
        }))
      )
    : of(undefined);
  readonly #showSideMenu$ = combineLatest([this.#routerDataShowSideMenu$, this.#isDesktop$]).pipe(
    map(([routerDataHasShowSideMenu, isDesktop]) => routerDataHasShowSideMenu && isDesktop)
  );

  public versions = toSignal(this.#versions$);
  public showSideMenu = toSignal(this.#showSideMenu$);
  public showOnlyFeVersion = !this.#config.banner;

  constructor() {
    this.init();
  }

  private init(): void {
    this.setTitle();
    this.setLocale();
    this.getBeVersion();
    this.prepareWebManifest();
    this.#userService.init();
    this.#gtagService.addGtmToDom();
  }

  private setTitle(): void {
    if (this.#config.titleTag) {
      this.#title.setTitle(`[${this.#config.titleTag}] ${this.#title.getTitle()}`);
    }
  }

  private setLocale(): void {
    registerLocaleData(LOCALE_GB);
  }

  private getBeVersion(): void {
    if (this.#config.showVersion) {
      this.#store.dispatch(MetaDataActions.getVersion());
    }
  }

  private prepareWebManifest(): void {
    const baseUri = this.#document.baseURI; // contains trailing slash

    const manifest = {
      name: APP_NAME,
      description: 'Web Frontend of Helix, the nomination platform of ElecLink',
      start_url: baseUri,
      display: 'standalone',
      icons: [
        {
          src: `${baseUri}assets/favicons/android-chrome-192x192.png`,
          sizes: '192x192',
          type: 'image/png',
        },
        {
          src: `${baseUri}assets/favicons/android-chrome-512x512.png`,
          sizes: '512x512',
          type: 'image/png',
        },
        {
          src: `${baseUri}assets/favicons/apple-touch-icon.png`,
          sizes: '180x180',
          type: 'image/png',
        },
      ],
      theme_color: '#ffffff',
      background_color: '#ffffff',
    };

    // add title based on configuration
    if (this.#config.titleTag) {
      manifest.name = `[${this.#config.titleTag}] ${manifest.name}`;
    }

    // set as data url
    const manifestDataUrl = `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
    this.#document.querySelector('head > #webmanifest')?.setAttribute('href', manifestDataUrl);
  }
}
